import React, { createContext, useContext, useState, useEffect, useMemo, useRef, useCallback } from "react";
import TextView from "./Typography";
import { Box } from "@mui/material";
import { generateRandomId, isEmpty } from "../../helpers/Functions";
import displayToast from "./DisplayToast";
import { FileHandlerProviderContext } from "../../contexts/contexts";
import { uploadFileOpenAI } from "../../app/crud/ai.crud";
import { useSelector } from "react-redux";

const MAX_FILE_SIZE_MB = 20; // Maximum file size in MB
const MAX_FILE_SIZE_BYTES = MAX_FILE_SIZE_MB * 1024 * 1024;

const EXTENSION_MIMETYPES_MAP = {
	pdf: ["application/pdf"],
	docx: ["application/vnd.openxmlformats-officedocument.wordprocessingml.document"],
	xlsx: ["application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"],
	csv: ["text/csv", "application/vnd.ms-excel"],
	json: ["application/json", "text/json"],
	jsonl: ["application/x-jsonlines", "application/jsonl", "text/jsonl"], // not standardized; allow common variants
	txt: ["text/plain"],
	pptx: ["application/vnd.openxmlformats-officedocument.presentationml.presentation"],
	jpeg: ["image/jpeg"],
	png: ["image/png"],
	gif: ["image/gif"],
	bmp: ["image/bmp"],
	tiff: ["image/tiff"],
	mp3: ["audio/mpeg"],
	wav: ["audio/wav", "audio/x-wav"],
	m4a: ["audio/mp4", "audio/x-m4a", "audio/m4a"],
	mp4: ["video/mp4"],
};

export const FileHandlerProvider = ({
	children,
	parentComponent = document.body, // optional ref
}) => {
	const currentCompany = useSelector((state) => state.auth.current_capabilities?.company);
	const companyAllowedAttachmentTypes = currentCompany?.settings?.allowed_attachments || [];

	const [withFileDrop, setWithFileDrop] = useState(false);
	const [files, setFiles] = useState([]);
	const [isDragging, setIsDragging] = useState(false);
	const [allowFileDrop, setAllowFileDrop] = useState(null);

	const fileInputAttachmentRef = useRef(null);
	const fileDropEvents = useRef({ handleDragOver: null, handleDragLeave: null, handleDrop: null });

	const companyAllowedTypesSet = useMemo(() => {
		return companyAllowedAttachmentTypes.map(type => `.${type.trim().toLowerCase()}`);
	}, [companyAllowedAttachmentTypes]);

	const isFilesUploading = useMemo(() => {
		if (isEmpty(files)) return false;
		
		return files.some((f) => !f.file.uploaded);
	}, [files]);
	
	const isFileObjectTypeAccepted = useCallback((fileType) => {
		if (isEmpty(fileType) || typeof fileType !== "string") {
			return false;
		}

		const normalized = fileType.trim().toLowerCase();

		// Build set of allowed MIME types from company settings
		const allowedValues = (companyAllowedAttachmentTypes || []).map(v => String(v).trim().toLowerCase());
		if (allowedValues.length === 0) return false;

		const allowedMimeSet = new Set();
		for (const val of allowedValues) {
			const mimes = EXTENSION_MIMETYPES_MAP[val];
			if (mimes && mimes.length) {
				for (const m of mimes) allowedMimeSet.add(m);
			}
		}

		return allowedMimeSet.has(normalized);
	}, [companyAllowedAttachmentTypes]);

	const fileToFileObj = (file) => {
		return {
			id: generateRandomId(),
			file,
		};
	}

	const syncFiles = (inputFiles = []) => {
		if (inputFiles.length === 0) {
			return;
		}

		const newFiles = [];

		for (const inpFile of inputFiles) {
			if (inpFile.size > MAX_FILE_SIZE_BYTES) {
				displayToast("error", `Too large: ${inpFile.name} (max ${MAX_FILE_SIZE_MB} MB).`);
				continue;
			}

			const ext = (inpFile.name?.split(".").pop() || "").toLowerCase();
			const extDisplay = ext ? `${ext}`.toUpperCase() : "unknown";

			if (!isFileObjectTypeAccepted(inpFile.type)) {
				displayToast("error", `Unsupported file type ${extDisplay}`);
				continue;
			}

			const fileWithMeta = {
				uploaded: false,
				file_id: "",
				file_name: inpFile.name,
				uploadParams: {
					inpFile,
					type: inpFile.type,
					name: inpFile.name,
					size: inpFile.size,
				},
			};

			newFiles.push(fileToFileObj(fileWithMeta));
		}
		setFiles((prev) => [...prev, ...newFiles]);
	};

	// Keep a ref to the latest syncFiles so event handlers always call the current version
	const syncFilesRef = useRef(syncFiles);
	useEffect(() => {
		syncFilesRef.current = syncFiles;
	}, [syncFiles]);

	const removeFile = (id) => {
		setFiles((prev) => prev.filter((f) => f.id !== id));
	};

	const clearFiles = () => {
		setFiles([]);
	};

	const openFileSelector = () => {
		fileInputAttachmentRef?.current?.click?.();
	}

	useEffect(() => {
		setWithFileDrop(Boolean(allowFileDrop));
	}, [allowFileDrop]);

	useEffect(() => {
		if (!parentComponent || !withFileDrop) {
			return;
		}

		const handleDragOver = (e) => {
			if (e.dataTransfer.types && e.dataTransfer.types.includes('Files')) {
				e.preventDefault();
				setIsDragging(true);
			}
		};


		const handleDragLeave = (e) => {
			e.preventDefault();
			setIsDragging(false);
		};

		const handleDrop = (e) => {
			e.preventDefault();
			setIsDragging(false);
			if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
				syncFilesRef.current(e.dataTransfer.files);
			}
		};
		
		parentComponent.addEventListener("dragover", handleDragOver);
		parentComponent.addEventListener("dragleave", handleDragLeave);
		parentComponent.addEventListener("drop", handleDrop);

		return () => {
			parentComponent.removeEventListener("dragover", handleDragOver);
			parentComponent.removeEventListener("dragleave", handleDragLeave);
			parentComponent.removeEventListener("drop", handleDrop);
		};
	}, [parentComponent, withFileDrop]);

	const nonUploadedFiles = useMemo(() => files.filter((f) => !f.file.uploaded), [files]);

	useEffect(() => {
		if (!isEmpty(nonUploadedFiles)) {
			let params = {
				ai_files: nonUploadedFiles.map((f) => f?.file?.uploadParams),
				company_id: currentCompany?.company_id,
			};

			uploadFileOpenAI(params).then((response) => {
				if (response.data.init[0].status === "error") {
					displayToast("error", response.data.init[0]["message"]);
				} else {
					let data = response.data.data;

					const uploadedMap = new Map();
					data.forEach((d) => uploadedMap.set(d.file_name, d));

					setFiles(
						files.map((file) => {
							const uploadedFile = uploadedMap.get(file.file.file_name);

							if (!isEmpty(uploadedFile)) {
								const uploadParams = file.file.uploadParams;

								const fileWithMeta = {
									uploaded: true,
									file_url: uploadedFile.file_u_r_l,
									file_id: uploadedFile.open_a_i_file_id,
									file_name: uploadParams.name,
									uploadParams: {
										inpFile: uploadParams.inpFile,
										type: uploadParams.type,
										name: uploadParams.name,
										size: uploadParams.size,
									},
								};

								return fileToFileObj(fileWithMeta);
							}

							return file;
						})
					);
				}
			});
		}
	}, [nonUploadedFiles]);

	return (
		<FileHandlerProviderContext.Provider value={{
			files,
			isFilesUploading,
			setFiles,
			isDragging,
			clearFiles,
			syncFiles,
			removeFile,
			nonUploadedFiles,
			openFileSelector,
			setAllowFileDrop,
		}}>
			<input
				ref={fileInputAttachmentRef}
				id="file-upload-input"
				type="file"
				multiple
				onChange={(e) => {
					syncFiles(e.target.files);
				}}
				hidden
				accept={companyAllowedTypesSet.join(",")}
			/>

			{children}

			{(isDragging) && (
				<Box
					sx={{
						position: "absolute",
						top: 0,
						left: 0,
						right: 0,
						bottom: 0,
						background: "rgba(0, 0, 0, 0.25)",
						border: "2px dashed var(--theme-primary)",
						fontWeight: 600,
						fontSize: "1rem",
						display: "flex",
						flexDirection: "column",
						alignItems: "center",
						justifyContent: "center",
						pointerEvents: "none",
						zIndex: 9999,
					}}
				>
					<TextView fontsize="xl" fontcolor="white" fontweight="semibold">Drop to upload</TextView>
					<TextView fontcolor="white" fontsize="medium" fontweight="medium">Your files will be processed instantly</TextView>
				</Box>
			)}
		</FileHandlerProviderContext.Provider>
	);
};
